'use client';
import { PremiumBackground } from '../effects/PremiumBackground';

import React from 'react';
import { motion } from 'framer-motion';
import { 
ArrowDown, 
  ShieldCheck, 
  Wrench, 
  TrendingUp, 
  CheckCircle2, 
  BookmarkCheck,
  Clock,
  Sparkles
} from 'lucide-react';

// Drop-in Link component for Vite React SPA
const Link = ({
  href,
  children,
  className,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) => (
  <a href={href} className={className} {...props}>
    {children}
  </a>
);

export interface StrategicForkProps {
  id?: string;
}

export function StrategicFork({ id = 'strategic-fork' }: StrategicForkProps = {}) {
  // Микроразметка Schema.org для нейросетей (SearchGPT, Perplexity, Яндекс Нейро, Google AI)
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Стратегическая развилка M&A в частной медицине: 3 маршрута после диагностики клиники",
    "provider": {
      "@type": "Organization",
      "name": "Structure & Partners"
    },
    "description": "Три сценария продажи стоматологической клиники или медицинского центра после экспресс-аудита: 1) Экзит As-Is с юридическим щитом от субсидиарной ответственности (ст. 406.1 ГК РФ); 2) Light Pre-Sale Prep (зачистка стоп-факторов по ПП № 736 и ЕГИСЗ); 3) Deep Capitalization (консолидация по 176-ФЗ, нормализация EBITDA до 7.5x, прирост чистых выплат до 80+ млн руб).",
    "serviceType": "M&A Deal Structuring / Exit Planning",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Институциональные сценарии экзита",
      "itemListElement": [
        {
          "@type": "Offer",
          "name": "Сценарий 1: Быстрый Экзит As-Is (1–2 месяца)",
          "description": "Продажа с открытыми рисками под защитой Disclosure Letter, Liability Cap и Tax Escrow. Мультипликатор 4.0x–4.5x."
        },
        {
          "@type": "Offer",
          "name": "Сценарий 2: Light Pre-Sale Prep (1–3 месяца)",
          "description": "Косметическая зачистка кадровых и лицензионных стоп-факторов. Защита базовой цены. Мультипликатор 5.0x–6.0x."
        },
        {
          "@type": "Offer",
          "name": "Сценарий 3: Deep Capitalization (4–9 месяцев)",
          "description": "Полномасштабный VDD по 604 точкам, амнистия 176-ФЗ, QoE Add-backs, внедрение S&P Clinic OS™ и Bidding Process. Мультипликатор 6.5x–7.5x EBITDA."
        }
      ]
    }
  };

  // Данные для сравнительной таблицы параметров
  const benchmarkRows = [
    {
      param: "Срок реализации",
      asIs: "1–2 месяца",
      light: "1–3 месяца",
      deep: "4–9 месяцев"
    },
    {
      param: "Трудозатраты собственника",
      asIs: "2–3 часа на старте",
      light: "5–10 часов",
      deep: "Минимальные (работает команда S&P)"
    },
    {
      param: "Устранение налоговых рисков",
      asIs: "Перенос в договор (SPA)",
      light: "Частичное (кадры, ЕГИСЗ)",
      deep: "Полное (Амнистия 176-ФЗ, 0% ставка)"
    },
    {
      param: "Мультипликатор к прибыли",
      asIs: "4.0x – 4.5x",
      light: "5.0x – 6.0x",
      deep: "6.5x – 7.5x EBITDA"
    },
    {
      param: "Формат закрытия сделки",
      asIs: "Asset Deal / Дисконт",
      light: "Share Deal / Вычет долгов",
      deep: "Премиальный Share Deal / Аукцион"
    }
  ];

  return (
    <section 
      id={id} 
      className="relative w-full py-24 md:py-32 bg-[#FAF8F5] text-[#141416] border-t border-[#EAE5DE] overflow-hidden"
      aria-label="Стратегическая развилка: выбор сценария"
    >
      {/* ТАКТИЛЬНЫЙ МАКРО-РЕЛЬЕФ */}
      <PremiumBackground variant={6} />

      
      

      <div id="strategic-fork-anchor" className="absolute -top-16 left-0" />
      
      {/* Light subtle archival paper gradient */}
      <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-b from-[#F2EDE4]/60 to-transparent pointer-events-none" />

      {/* Скрытая разметка JSON-LD для нейросетей */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 flex flex-col gap-16 md:gap-20">
        
        {/* =========================================================================
            1. НАДЗАГОЛОВОК, H2 И ПОДЗАГОЛОВОК-МАНИФЕСТ
            ========================================================================= */}
        <div className="flex flex-col gap-6 max-w-4xl">
          <div className="luxury-eyebrow text-[#8C6D37] flex items-center gap-2.5 font-bold tracking-[0.2em] uppercase">
            <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37]" />
            ЭТАЖ 7 // СТРАТЕГИЧЕСКАЯ РАЗВИЛКА // ВЫБОР СЦЕНАРИЯ СДЕЛКИ
          </div>

          <h2 className="text-3xl sm:text-5xl lg:text-[54px] font-serif font-semibold tracking-tight leading-[1.1] text-[#141416]">
            Шаг 2. Три маршрута после постановки диагноза: <br />
            <span className="text-[#64748B] italic font-serif">выбор институционального сценария.</span>
          </h2>

          <p className="text-[16px] sm:text-[17px] text-[#334155] leading-[1.65] font-sans font-normal max-w-3xl border-l-[4px] border-[#8C6D37] pl-6 mt-4">
            Когда риски оцифрованы и переведены в рубли, у вас больше нет иллюзий. <br />
            Мы не навязываем шаблонных решений. Мы садимся за стол и выбираем стратегию под ваши 
            личные цели, уровень усталости и желаемый объем чистых денег.
          </p>
        </div>

        {/* =========================================================================
            2. МАТРИЦА 3-Х ИНСТИТУЦИОНАЛЬНЫХ СЦЕНАРИЕВ (CARDS GRID)
            ========================================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          
          {/* КАРТОЧКА 1: ЭКЗИТ AS-IS */}
          <div className="p-8 sm:p-10 flex flex-col justify-between gap-6 bg-white border border-[#EAE5DE] shadow-xs rounded-[2px]">
            <div className="flex flex-col gap-6">
              
              {/* Верхняя строка + Шильдик */}
              <div className="flex flex-col gap-3 pb-5 border-b border-[#EAE5DE]">
                <div className="flex items-center justify-between">
                  <span className="luxury-eyebrow text-[#64748B] font-bold uppercase tracking-wider">
                    СЦЕНАРИЙ 1
                  </span>
                  <ShieldCheck size={16} className="text-[#64748B]" />
                </div>
                <h3 className="text-2xl font-serif font-semibold text-[#141416] leading-snug">
                  «Быстрый Экзит As-Is» <br />
                  <span className="text-[14px] font-sans font-normal text-[#64748B]">(Продажа с открытыми рисками)</span>
                </h3>
                <span className="font-mono text-[10px] text-[#64748B] bg-[#F7F4EE] border border-[#EAE5DE] px-2.5 py-1 rounded-[2px] w-fit font-bold tracking-wider mt-1 uppercase">
                  СРОК: 1–2 МЕСЯЦА // БЫСТРЫЙ ВЫХОД
                </span>
              </div>

              {/* Для кого */}
              <div className="text-[14px] text-[#334155] leading-relaxed font-sans font-normal">
                <strong className="text-[#141416] font-bold block mb-1">Для кого:</strong>
                Вы эмоционально выгорели, устали от медицины и хотите зафиксировать деньги прямо сейчас. 
                У вас нет желания и ресурса тратить полгода на перестройку клиники.
              </div>

              {/* Что делаем мы */}
              <div className="flex flex-col gap-2.5 text-[13px] text-[#334155] leading-relaxed font-sans font-normal border-t border-[#EAE5DE] pt-5">
                <strong className="text-[#141416] block font-bold">
                  Что делаем мы (Юридический щит продавца):
                </strong>
                <p>
                  Мы не обманываем покупателя. Мы открыто раскрываем выявленные дефекты в специальном юридическом приложении к договору <strong>(Раскрывающее письмо / Disclosure Letter)</strong>.
                </p>
                <p>
                  Конструируем договор купли-продажи (SPA) с жестким ограничением ответственности продавца <strong>(Liability Cap)</strong>.
                </p>
                <p>
                  Устанавливаем разумный лимит заморозки денег в банке <strong>(Tax Escrow)</strong> и полностью защищаем вас от личной субсидиарной и уголовной ответственности после передачи ключей.
                </p>
              </div>
            </div>

            {/* Финансовый результат */}
            <div className="p-4 bg-[#F7F4EE] border border-[#EAE5DE] rounded-[2px] text-[13px] text-[#334155] font-sans font-normal leading-relaxed mt-4">
              <strong className="luxury-eyebrow text-[#141416] font-bold block mb-1 uppercase tracking-wider">
                ФИНАНСОВЫЙ РЕЗУЛЬТАТ:
              </strong>
              Продажа бизнеса по текущей рыночной цене с дисконтом за риски, но с абсолютной гарантией того, что к вам не придут с судебными исками через 2 года.
            </div>
          </div>

          {/* КАРТОЧКА 2: LIGHT PRE-SALE PREP */}
          <div className="p-8 sm:p-10 flex flex-col justify-between gap-6 bg-white border border-[#EAE5DE] shadow-xs rounded-[2px]">
            <div className="flex flex-col gap-6">
              
              {/* Верхняя строка + Шильдик */}
              <div className="flex flex-col gap-3 pb-5 border-b border-[#EAE5DE]">
                <div className="flex items-center justify-between">
                  <span className="luxury-eyebrow text-[#64748B] font-bold uppercase tracking-wider">
                    СЦЕНАРИЙ 2
                  </span>
                  <Wrench size={16} className="text-[#8C6D37]" />
                </div>
                <h3 className="text-2xl font-serif font-semibold text-[#141416] leading-snug">
                  «Light Pre-Sale Prep» <br />
                  <span className="text-[14px] font-sans font-normal text-[#64748B]">(Косметическая зачистка стоп-факторов)</span>
                </h3>
                <span className="font-mono text-[10px] text-[#8C6D37] bg-[#8C6D37]/10 border border-[#8C6D37]/30 px-2.5 py-1 rounded-[2px] w-fit font-bold tracking-wider mt-1 uppercase">
                  СРОК: 1–3 МЕСЯЦА // ЗАЩИТА БАЗОВОЙ ЦЕНЫ
                </span>
              </div>

              {/* Для кого */}
              <div className="text-[14px] text-[#334155] leading-relaxed font-sans font-normal">
                <strong className="text-[#141416] font-bold block mb-1">Для кого:</strong>
                Клиника в хорошем операционном состоянии, но диагностика выявила 3–4 очевидные «мины» 
                (недочеты в договорах с пациентами по ПП № 736, врачи на самозанятости, хвосты в ЕГИСЗ), 
                за которые покупатель гарантированно собьет 15–20% цены.
              </div>

              {/* Что делаем мы */}
              <div className="flex flex-col gap-2.5 text-[13px] text-[#334155] leading-relaxed font-sans font-normal border-t border-[#EAE5DE] pt-5">
                <strong className="text-[#141416] block font-bold">
                  Что делаем мы (Точечное устранение блокеров):
                </strong>
                <p>
                  Проводим быстрый локальный ремонт: легализуем кадровые договоры с врачами, обновляем формы согласий пациентов (ИДС), настраиваем выгрузку электронных карт в РЭМД ЕГИСЗ.
                </p>
                <p>
                  Устраняем поводы для срыва сделки за день до нотариуса <strong>(Deal Breakers)</strong>.
                </p>
                <p>
                  Упаковываем аккуратный инвестиционный тизер для целевых покупателей.
                </p>
              </div>
            </div>

            {/* Финансовый результат */}
            <div className="p-4 bg-[#F7F4EE] border border-[#EAE5DE] rounded-[2px] text-[13px] text-[#334155] font-sans font-normal leading-relaxed mt-4">
              <strong className="luxury-eyebrow text-[#141416] font-bold block mb-1 uppercase tracking-wider">
                ФИНАНСОВЫЙ РЕЗУЛЬТАТ:
              </strong>
              Ликвидация оснований для агрессивного торга. Фиксация твердой рыночной оценки по мультипликатору <strong>5.0x – 6.0x годовой прибыли</strong> без затяжной перестройки процессов.
            </div>
          </div>

          {/* КАРТОЧКА 3: DEEP CAPITALIZATION (ФОКУСНАЯ) */}
          <div className="p-8 sm:p-10 border border-[#8C6D37]/50 bg-[#8C6D37]/10 flex flex-col justify-between gap-6 relative shadow-xs rounded-[2px]">
            
            {/* Бронзовый шильдик максимизации */}
            <div className="absolute -top-3 right-6 bg-[#8C6D37] text-white font-mono text-[10px] px-3.5 py-1 uppercase tracking-[0.2em] font-bold rounded-none shadow-sm flex items-center gap-1.5">
              <Sparkles size={11} /> РЕКОМЕНДУЕМЫЙ ФОКУС
            </div>

            <div className="flex flex-col gap-6 pt-2">
              
              {/* Верхняя строка + Шильдик */}
              <div className="flex flex-col gap-3 pb-5 border-b border-[#8C6D37]/20">
                <div className="flex items-center justify-between">
                  <span className="luxury-eyebrow text-[#8C6D37] font-bold uppercase tracking-wider">
                    СЦЕНАРИЙ 3
                  </span>
                  <TrendingUp size={16} className="text-[#8C6D37]" />
                </div>
                <h3 className="text-2xl font-serif font-semibold text-[#141416] leading-snug">
                  «Deep Capitalization» <br />
                  <span className="text-[14px] font-sans font-normal text-[#8C6D37]">(Капитальная предпродажная трансформация)</span>
                </h3>
                <span className="font-mono text-[10px] text-[#8C6D37] bg-white/80 border border-[#8C6D37]/30 px-2.5 py-1 rounded-[2px] w-fit font-bold tracking-wider mt-1 uppercase">
                  СРОК: 4–9 МЕСЯЦЕВ // МАКСИМИЗАЦИЯ КАПИТАЛИЗАЦИИ
                </span>
              </div>

              {/* Для кого */}
              <div className="text-[14px] text-[#334155] leading-relaxed font-sans font-normal">
                <strong className="text-[#141416] font-bold block mb-1">Для кого:</strong>
                Ваша цель — забрать с рынка максимальный чек и продать актив стратегическому инвестору 
                (крупной федеральной сети или фонду) по премиальному мультипликатору <strong>6.5x – 7.5x EBITDA</strong>.
              </div>

              {/* Что делаем мы */}
              <div className="flex flex-col gap-2.5 text-[13px] text-[#141416] leading-relaxed font-sans font-normal border-t border-[#8C6D37]/20 pt-5">
                <strong className="text-[#8C6D37] block font-bold">
                  Что делаем мы (Тяжелая финансовая и юридическая инженерия):
                </strong>
                <p>
                  • Проводим полномасштабный аудит <strong>(Vendor Due Diligence)</strong> по всем 604 точкам контроля;
                </p>
                <p>
                  • Реализуем сценарий безналоговой консолидации дробления по Амнистии <strong>(ФЗ № 176-ФЗ)</strong>, списывая исторические риски за 2022–2024 годы в ноль;
                </p>
                <p>
                  • Строим мост нормализации прибыли <strong>(QoE Add-backs)</strong>, легально возвращая ваши личные расходы обратно в расчетную прибыль бизнеса;
                </p>
                <p>
                  • Внедряем систему операционной автономии <strong>(S&P Clinic OS™)</strong>, привязывая ведущих хирургов опционами и ликвидируя зависимость бизнеса от основателя;
                </p>
                <p>
                  • Организуем закрытый конкурс предложений между институциональными покупателями <strong>(Bidding Process)</strong>.
                </p>
              </div>
            </div>

            {/* Финансовый результат */}
            <div className="p-4 bg-white/90 border border-[#8C6D37]/30 rounded-[2px] text-[13px] text-[#141416] font-sans font-normal leading-relaxed mt-4 shadow-xs">
              <strong className="luxury-eyebrow text-[#8C6D37] font-bold block mb-1 uppercase tracking-wider">
                ФИНАНСОВЫЙ РЕЗУЛЬТАТ:
              </strong>
              Прирост чистых выплат на руки продавцу <strong>от 30 до 80+ миллионов рублей</strong> по сравнению с продажей в исходном состоянии.
            </div>
          </div>

        </div>

        {/* =========================================================================
            3. СРАВНИТЕЛЬНЫЙ ОРИЕНТИР СЦЕНАРИЕВ (DECISION BENCHMARK TABLE)
            ========================================================================= */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <span className="luxury-eyebrow text-[#64748B] font-bold tracking-wider uppercase">
              СРАВНИТЕЛЬНЫЙ ОРИЕНТИР СЦЕНАРИЕВ // DECISION BENCHMARK TABLE
            </span>
            <span className="font-mono text-[10px] text-[#64748B] block sm:hidden font-bold">
              ← Прокручивайте таблицу пальцем горизонтально →
            </span>
          </div>

          <div className="bg-white rounded-[2px] overflow-x-auto border border-[#EAE5DE] shadow-xs">
            <table className="w-full text-left border-collapse min-w-[640px]">
              <thead>
                <tr className="bg-[#F7F4EE] border-b border-[#EAE5DE] text-[11px] font-mono text-[#64748B] uppercase tracking-widest font-bold">
                  <th className="py-4 px-6">Параметр сравнения</th>
                  <th className="py-4 px-6 border-l border-[#EAE5DE]">Сценарий 1: As-Is</th>
                  <th className="py-4 px-6 border-l border-[#EAE5DE]">Сценарий 2: Light Prep</th>
                  <th className="py-4 px-6 text-[#8C6D37] border-l border-[#EAE5DE] bg-[#8C6D37]/10">Сценарий 3: Deep Overhaul</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EAE5DE] text-[13px] font-sans font-normal">
                {benchmarkRows.map((row, idx) => (
                  <tr key={idx} className="hover:bg-[#FAF8F5] transition-colors">
                    <td className="py-4 px-6 font-bold text-[#141416] bg-[#FAF8F5]/30">
                      {row.param}
                    </td>
                    <td className="py-4 px-6 text-[#334155] font-mono text-[12px] border-l border-[#EAE5DE]">
                      {row.asIs}
                    </td>
                    <td className="py-4 px-6 text-[#334155] font-mono text-[12px] border-l border-[#EAE5DE]">
                      {row.light}
                    </td>
                    <td className="py-4 px-6 font-bold text-[#141416] font-mono text-[12px] bg-[#8C6D37]/5 border-l border-[#EAE5DE]">
                      {row.deep}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* =========================================================================
            4. ИНСТИТУЦИОНАЛЬНЫЙ ТЕЗИС ОБ АЛЬТЕРНАТИВЕ (СЦЕНАРИЙ 4)
            ========================================================================= */}
        <div className="p-8 sm:p-10 bg-white border border-[#EAE5DE] shadow-xs rounded-[2px] flex flex-col gap-3 relative overflow-hidden">
          <div className="absolute inset-y-0 left-0 w-1.5 bg-[#8C6D37]" />
          <div className="luxury-eyebrow text-[#8C6D37] flex items-center gap-2 font-bold pl-4 tracking-wider uppercase">
            <BookmarkCheck size={16} className="text-[#8C6D37]" />
            <span>СЦЕНАРИЙ 4: «ТРАНСФОРМАЦИЯ ДЛЯ СЕБЯ (БЕЗ ПРОДАЖИ)»</span>
          </div>
          <p className="text-[15px] sm:text-[16px] text-[#334155] leading-relaxed font-sans font-normal pl-4">
            Если в процессе аудита и наведения порядка вы решите не продавать клинику — вы получаете 
            полностью автономный, очищенный от проверок бизнес, работающий по стандартам регулярного менеджмента 
            и приносящий стабильные дивиденды без вашего круглосуточного присутствия.
          </p>
        </div>

        {/* =========================================================================
            5. НАВИГАТОР ПЕРЕХОДА К БЛОКУ 8
            ========================================================================= */}
        <div className="flex justify-center pt-4">
          <Link 
            href="#remediation" 
            className="group inline-flex items-center gap-2 font-mono text-[11px] text-[#64748B] hover:text-[#141416] uppercase tracking-[0.15em] font-semibold transition-all py-3 px-6 hover:bg-black/5 border border-transparent hover:border-[#EAE5DE] rounded-[2px]"
          >
            <span>[ ПЕРЕЙТИ К ЭТАЖУ 8: ТОЧЕЧНОЕ УСТРАНЕНИЕ ДЕФЕКТОВ ↓ ]</span>
          </Link>
        </div>

      </div>
    </section>
  );
}
